import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import './Welcome.css';
import '@fontsource/bebas-neue';
import { useTranslation } from 'react-i18next';
import { FriendsIcon } from './friends';
import { SettingsMenu } from './settings';
import { useMusic } from './hooks/useMusic';
import { MAIN_MENU_MUSIC_PATH } from './config/mainMenuMusic';
import { playSoundEffect } from './hooks/useSoundEffects';
import { API_URL } from './config/apiConfig';
import StrategyPreferenceRewardModal from './StrategyPreferenceRewardModal';
import { TutorialMessageBox, TutorialReplayButton, useMenuTutorialReplay } from './tutorial/index';
import { useContainerScale } from './hooks/useViewportScale';
import { isElectronClient } from './utils/isElectronClient';
import {
  WELCOME_VIDEO_PATH,
  WELCOME_VIDEO_POSTER_PATH,
} from './config/welcomeAssets';
import AchievementUnlockedToast from './notifications/AchievementUnlockedToast';
import { usePendingAchievementToasts } from './hooks/usePendingAchievementToasts';
import { useTutorialStatus } from './hooks/useTutorialStatus';
import WelcomeExploreUnknownLeaderboard from './WelcomeExploreUnknownLeaderboard';
import WelcomeHonorHallMovements from './WelcomeHonorHallMovements';
import WelcomeLatestRemnants from './WelcomeLatestRemnants';
import './WelcomeLeftStack.css';

const STRATEGY_CHOICES = [
  { key: 'aggressive', value: 'Aggressive' },
  { key: 'greedy', value: 'Greedy' },
  { key: 'patient', value: 'Patient' },
  { key: 'controlling', value: 'Controlling' },
  { key: 'creative', value: 'Creative' }
];

function Welcome({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [appVersion, setAppVersion] = useState('0.1.8');
  const [testersOnlineCount, setTestersOnlineCount] = useState(null);
  const [testersOnlineFetchFailed, setTestersOnlineFetchFailed] = useState(false);
  
  // Viewport scaling for responsive design
  const { 
    scale: viewportScale, 
    offsetX: viewportOffsetX, 
    offsetY: viewportOffsetY,
    baseWidth: containerBaseWidth,
    baseHeight: containerBaseHeight,
    isMobile: isContainerMobile
  } = useContainerScale();
  
  // Strategy preference and tutorial state
  const [hasSelectedStrategyPreference, setHasSelectedStrategyPreference] = useState(null); // null = not checked yet
  const {
    seenMessages: seenTutorialMessages,
    isAlwaysShowUser,
    shouldShowTutorial,
    markSeenLocally,
  } = useTutorialStatus(user);
  const [showTutorial1, setShowTutorial1] = useState(false);
  const [showTutorial2, setShowTutorial2] = useState(false);
  const [showStrategyRewardModal, setShowStrategyRewardModal] = useState(false);
  const [grantedOrders, setGrantedOrders] = useState([]);
  const [selectingPreference, setSelectingPreference] = useState(false);
  const { isReplayRequest: isReplayingWelcome } = useMenuTutorialReplay('welcome', {
    enabled: Boolean(user),
    onReplay: () => {
      setShowTutorial2(false);
      setShowStrategyRewardModal(false);
      setShowTutorial1(true);
    },
  });
  const { isReplayRequest: isReplayingCommandHub } = useMenuTutorialReplay('command_hub', {
    enabled: Boolean(user),
    onReplay: () => {
      setShowTutorial1(false);
      setShowStrategyRewardModal(false);
      setShowTutorial2(true);
    },
  });
  const welcomeVideoRef = useRef(null);
  const [welcomeBgVideoPlaying, setWelcomeBgVideoPlaying] = useState(false);
  const isWebClient = !isElectronClient();
  const [welcomeVideoEnabled, setWelcomeVideoEnabled] = useState(!isWebClient);

  const kickWelcomeBackgroundVideo = useCallback(() => {
    const el = welcomeVideoRef.current;
    if (!el) return;
    el.muted = true;
    el.defaultMuted = true;
    el.playsInline = true;
    el.setAttribute('playsinline', '');
    el.setAttribute('webkit-playsinline', '');
    el.play().catch(() => {});
  }, []);
  
  // Function to fetch version from web (version.json)
  const fetchWebVersion = async () => {
    try {
      const response = await fetch('/version.json');
      if (response.ok) {
        const data = await response.json();
        if (data.version) {
          setAppVersion(data.version);
        }
      } else {
        console.warn('Could not fetch version.json, using default');
      }
    } catch (err) {
      console.warn('Could not fetch app version:', err);
    }
  };
  
  // Get app version from Electron or web
  useEffect(() => {
    // Try Electron first
    if (window.electron && window.electron.getAppVersion) {
      window.electron.getAppVersion().then(version => {
        setAppVersion(version);
      }).catch(err => {
        console.warn('Could not get app version from Electron:', err);
        // Fallback to web version fetch
        fetchWebVersion();
      });
    } else {
      // Web version: fetch from version.json
      fetchWebVersion();
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const POLL_MS = 12000;

    const loadCount = async () => {
      try {
        const response = await fetch(`${API_URL}/testers-online`);
        if (!response.ok) {
          const errText = await response.text().catch(() => response.statusText);
          throw new Error(`testers-online HTTP ${response.status}: ${errText}`);
        }
        const data = await response.json();
        if (typeof data.count !== 'number' || !Number.isFinite(data.count)) {
          throw new Error('testers-online response missing numeric count');
        }
        if (!cancelled) {
          setTestersOnlineCount(Math.max(0, Math.floor(data.count)));
          setTestersOnlineFetchFailed(false);
        }
      } catch (err) {
        console.error('Welcome: testers-online fetch failed:', err);
        if (!cancelled) {
          setTestersOnlineFetchFailed(true);
        }
      }
    };

    loadCount();
    const id = setInterval(loadCount, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // Fetch strategy preference status and tutorial status
  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const prefRes = await fetch(`${API_URL}/profile/strategy-preference-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (prefRes.ok) {
        const data = await prefRes.json();
        setHasSelectedStrategyPreference(data.hasSelected || false);
      }
    } catch (err) {
      console.error('Error fetching status:', err);
    }
  };

  useEffect(() => {
    if (user) {
      fetchStatus();
    }
  }, [user]);

  // Web: defer heavy welcome video until the menu shell has painted (poster shows first).
  useEffect(() => {
    if (!isWebClient) {
      return undefined;
    }

    const enableVideo = () => setWelcomeVideoEnabled(true);
    if (typeof window.requestIdleCallback === 'function') {
      const idleId = window.requestIdleCallback(enableVideo, { timeout: 1500 });
      return () => window.cancelIdleCallback(idleId);
    }

    const timeoutId = window.setTimeout(enableVideo, 500);
    return () => window.clearTimeout(timeoutId);
  }, [isWebClient]);

  // Android WebView: kick muted inline playback and only fade video in once frames run (avoids giant play icon)
   useEffect(() => {
    if (!welcomeVideoEnabled) {
      return undefined;
    }

    const el = welcomeVideoRef.current;
    if (!el) return undefined;

    const onPlaying = () => setWelcomeBgVideoPlaying(true);

    el.addEventListener('playing', onPlaying);
    kickWelcomeBackgroundVideo();
    el.addEventListener('loadeddata', kickWelcomeBackgroundVideo);
    el.addEventListener('loadedmetadata', kickWelcomeBackgroundVideo);
    el.addEventListener('canplay', kickWelcomeBackgroundVideo);

    const failsafe = window.setTimeout(() => {
      setWelcomeBgVideoPlaying(true);
    }, 6000);

    return () => {
      window.clearTimeout(failsafe);
      el.removeEventListener('playing', onPlaying);
      el.removeEventListener('loadeddata', kickWelcomeBackgroundVideo);
      el.removeEventListener('loadedmetadata', kickWelcomeBackgroundVideo);
      el.removeEventListener('canplay', kickWelcomeBackgroundVideo);
    };
  }, [kickWelcomeBackgroundVideo, welcomeVideoEnabled]);

  // ✅ DEV: Check if dev starting rewards are passed via state (for dev demo)
  useEffect(() => {
    if (location.state?.devStartingRewards && Array.isArray(location.state.devStartingRewards)) {
      setGrantedOrders(location.state.devStartingRewards);
      setShowStrategyRewardModal(true);
      // Clear the location state to prevent re-adding on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  // Show Tutorial 1: normal users when they haven't selected; isAlwaysShowUser (e.g. player1) always
  useEffect(() => {
    const shouldShow = isAlwaysShowUser || (hasSelectedStrategyPreference === false && shouldShowTutorial('welcome'));
    if (user && shouldShow && !location.state?.devStartingRewards) {
      const timer = setTimeout(() => setShowTutorial1(true), 500);
      return () => clearTimeout(timer);
    }
  }, [user, hasSelectedStrategyPreference, shouldShowTutorial, isAlwaysShowUser, location.state]);

  const handleSelectStrategyPreference = async (preference) => {
    if (selectingPreference) return;
    
    setSelectingPreference(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('You must be logged in to select a strategy preference. Please log in again.');
        return;
      }

      if (isReplayingWelcome) {
        setShowTutorial1(false);
        playSoundEffect('/sounds/click.mp3');
        setSelectingPreference(false);
        return;
      }

      if (isAlwaysShowUser && hasSelectedStrategyPreference) {
        setShowTutorial1(false);
        setShowTutorial2(true);
        playSoundEffect('/sounds/click.mp3');
        setSelectingPreference(false);
        return;
      }

      const res = await fetch(`${API_URL}/profile/select-strategy-preference`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ preference }),
      });
      
      if (res.ok) {
        const data = await res.json();
        setHasSelectedStrategyPreference(true);
        setShowTutorial1(false);
        setGrantedOrders(data.grantedOrders || []);
        setShowStrategyRewardModal(true);
        playSoundEffect('/sounds/click.mp3');

        await fetch(`${API_URL}/tutorial/mark-seen`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ messageKey: 'welcome' }),
        });
        markSeenLocally('welcome');
      } else {
        const errorData = await res.json().catch(() => ({ error: `HTTP ${res.status}: ${res.statusText}` }));
        console.error('Strategy preference selection failed:', errorData);
        alert(errorData.error || `Failed to select strategy preference (${res.status})`);
      }
    } catch (err) {
      console.error('Error selecting strategy preference:', err);
      alert(`Failed to select strategy preference: ${err.message || 'Network error'}`);
    } finally {
      setSelectingPreference(false);
    }
  };

  const handleRewardModalClose = () => {
    playSoundEffect('/sounds/click.mp3');
    setShowStrategyRewardModal(false);
    if (shouldShowTutorial('command_hub')) {
      setShowTutorial2(true);
    }
  };

  const handleTutorial2Continue = async () => {
    if (!isReplayingCommandHub) {
      const token = localStorage.getItem('token');
      if (token) {
        await fetch(`${API_URL}/tutorial/mark-seen`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ messageKey: 'command_hub' }),
        });
        markSeenLocally('command_hub');
      }
    }
    setShowTutorial2(false);
  };
  
  useMusic(MAIN_MENU_MUSIC_PATH);
  const { current: pendingToast, dismissCurrent: dismissPendingToast } = usePendingAchievementToasts();

  // Shared button style with welcome_buttons.png background
  // Smaller on mobile (932x430 base)
  const buttonStyle = {
    backgroundImage: 'url(/images/welcome_buttons.png)',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundColor: 'transparent',
    position: 'relative',
    zIndex: 1,
    width: isContainerMobile ? '130px' : '300px',
    height: isContainerMobile ? '42px' : '100px',
    minWidth: isContainerMobile ? '130px' : '300px',
    minHeight: isContainerMobile ? '42px' : '100px',
    padding: isContainerMobile ? '2px 4px' : undefined
  };

  const buttonTextStyle = {
    position: 'relative',
    zIndex: 2,
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    textAlign: 'center',
    lineHeight: isContainerMobile ? '1.0' : '1.2',
    fontSize: isContainerMobile ? '9px' : 'inherit',
    fontWeight: isContainerMobile ? '500' : undefined,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: isContainerMobile ? '120px' : undefined,
    display: isContainerMobile ? '-webkit-box' : undefined,
    WebkitLineClamp: isContainerMobile ? 2 : undefined,
    WebkitBoxOrient: isContainerMobile ? 'vertical' : undefined
  };

  return (
    <div className="welcome-viewport-wrapper">
      {pendingToast && (
        <AchievementUnlockedToast
          titleId={pendingToast.titleId}
          titleName={pendingToast.titleName}
          color={pendingToast.color}
          achievementKey={pendingToast.achievementKey}
          onClose={dismissPendingToast}
        />
      )}
      {/* Poster still frame — visible until looping video fades in */}
      <div
        className="welcome-background-poster"
        style={{ backgroundImage: `url(${WELCOME_VIDEO_POSTER_PATH})` }}
        aria-hidden
      />
      {welcomeVideoEnabled ? (
      <video
        ref={welcomeVideoRef}
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        controls={false}
        disablePictureInPicture
        disableRemotePlayback
        className={`welcome-fullscreen-video${welcomeBgVideoPlaying ? ' welcome-fullscreen-video--playing' : ''}`}
      >
        <source src={WELCOME_VIDEO_PATH} type="video/mp4" />
        {t('welcome.browserNoVideo')}
      </video>
      ) : null}
      
      {/* Viewport container - scales to fit screen */}
      <div 
        className="welcome-viewport-container"
        style={{
          width: `${containerBaseWidth}px`,
          height: `${containerBaseHeight}px`,
          transformOrigin: '0 0',
          transform: `translate(${viewportOffsetX}px, ${viewportOffsetY}px) scale(${viewportScale})`,
        }}
      >
        {(user?.username || user) && (
          <div style={{ 
            position: 'absolute', 
            top: isContainerMobile ? 5 : 10, 
            left: isContainerMobile ? 5 : 10, 
            zIndex: 1000, 
            display: 'flex', 
            alignItems: 'center',
            transform: isContainerMobile ? 'scale(0.8)' : 'none',
            transformOrigin: 'top left'
          }}>
            <FriendsIcon />
            <SettingsMenu onLogout={() => {
              playSoundEffect('/sounds/click.mp3');
              onLogout();
              navigate('/');
            }} />
            <span style={{ color: 'white', marginRight: '10px', fontSize: isContainerMobile ? '12px' : 'inherit' }}>{user?.username || user}</span>
          </div>
        )}

      <div className={`welcome-left-stack${isContainerMobile ? ' welcome-left-stack--mobile' : ''}`}>
        <WelcomeExploreUnknownLeaderboard isMobile={isContainerMobile} />
        <WelcomeHonorHallMovements
          isMobile={isContainerMobile}
          isDevPlayer1={user?.username === 'player1'}
        />
        <WelcomeLatestRemnants isMobile={isContainerMobile} />
      </div>

      {/* Logo - positioned in viewport space */}
      <img 
        src="/images/logo.png" 
        alt="Chronicles of the Dying: Anthology" 
        style={{
          position: 'absolute',
          top: isContainerMobile ? '10px' : '40px',
          left: isContainerMobile ? '700px' : '1460px', /* Mobile: right side, Desktop: 1460px */
          transform: 'translateX(-50%)',
          height: 'auto',
          width: isContainerMobile ? '200px' : 'auto',
          zIndex: 1,
          objectFit: 'contain'
        }}
      />

      <div 
        className="button-overlay"
        style={isContainerMobile ? {
          left: '466px', /* Center of 932px */
          bottom: '10px',
          gap: '0px',
          flexWrap: 'nowrap',
          justifyContent: 'center',
          width: '900px'
        } : {}}
      >
        <button 
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            navigate('/strategy');
          }}
          style={{...buttonStyle, margin: isContainerMobile ? '0 -15px' : '0 -50px'}}
        >
          <span style={{...buttonTextStyle, whiteSpace: 'pre-line'}}>
            {t('welcome.prepareStrategy')}
          </span>
        </button>
        <button 
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            navigate('/command-center');
          }}
          style={{...buttonStyle, margin: isContainerMobile ? '0 -15px' : '0 -50px'}}
        >
          <span style={{...buttonTextStyle, whiteSpace: 'pre-line'}}>
            {t('welcome.findStrategies')}
          </span>
        </button>
        <button 
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            navigate('/honor-hall');
          }}
          style={{...buttonStyle, margin: isContainerMobile ? '0 -15px' : '0 -50px'}}
        >
          <span style={{...buttonTextStyle, whiteSpace: 'pre-line', fontSize: isContainerMobile ? '8px' : 'inherit'}}>
            {t('welcome.honorHall')}
          </span>
        </button>
        <button 
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            navigate('/battleoverview');
          }}
          style={{...buttonStyle, margin: isContainerMobile ? '0 -15px' : '0 -50px'}}
        >
          <span style={buttonTextStyle}>{t('welcome.battle')}</span>
        </button>
        <button 
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            navigate('/lore');
          }}
          style={{...buttonStyle, margin: isContainerMobile ? '0 -15px' : '0 -50px'}}
        >
          <span style={buttonTextStyle}>{t('welcome.lore')}</span>
        </button>
        <button 
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            navigate('/profile');
          }}
          style={{...buttonStyle, margin: isContainerMobile ? '0 -15px' : '0 -50px'}}
        >
          <span style={buttonTextStyle}>{t('welcome.profile')}</span>
        </button>
        <button 
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            navigate('/bank');
          }}
          style={{...buttonStyle, margin: isContainerMobile ? '0 -15px' : '0 -50px'}}
        >
          <span style={buttonTextStyle}>{t('welcome.bank')}</span>
        </button>
      </div>
      
      {/* Testers online + version (lower right) */}
      <div style={{
        position: 'absolute',
        bottom: isContainerMobile ? 10 : 20,
        right: isContainerMobile ? 10 : 20,
        zIndex: 1000,
        color: 'rgba(255, 255, 255, 0.6)',
        fontSize: isContainerMobile ? '0.7em' : '0.9em',
        fontFamily: 'Exo 2, sans-serif',
        textShadow: '0 1px 3px rgba(0, 0, 0, 0.5)',
        textAlign: 'right',
        lineHeight: 1.35
      }}>
        <div>
          Testers online:{' '}
          {testersOnlineFetchFailed && testersOnlineCount === null
            ? '—'
            : testersOnlineCount === null
              ? '…'
              : testersOnlineCount}
        </div>
        <div>v{appVersion}</div>
      </div>
      </div>{/* Close welcome-viewport-container */}

      <TutorialReplayButton
        user={user}
        onReplay={() => {
          setShowTutorial2(false);
          setShowStrategyRewardModal(false);
          setShowTutorial1(true);
        }}
      />

      {/* Tutorial rendered via portal to document.body so it centers on true viewport */}
      {showTutorial1 && createPortal(
        <TutorialMessageBox
          image="/images/tutorial_welcome.png"
          text={t('welcome.tutorial.welcome')}
          choices={STRATEGY_CHOICES}
          onSelect={handleSelectStrategyPreference}
          onCancel={() => setShowTutorial1(false)}
          showCancel={true}
          disabled={selectingPreference}
          voiceKey="welcome.tutorial.welcome"
        />,
        document.body
      )}

      {showTutorial2 && createPortal(
        <TutorialMessageBox
          image="/images/tutorial_wait.png"
          text={t('welcome.tutorial.commandHub')}
          onContinue={handleTutorial2Continue}
          voiceKey="welcome.tutorial.commandHub"
        />,
        document.body
      )}

      <StrategyPreferenceRewardModal
        isOpen={showStrategyRewardModal}
        onClose={handleRewardModalClose}
        grantedOrders={grantedOrders}
      />
    </div>
  );
}

export default Welcome;